
import React, { useState, useEffect, useRef } from "react";
import "../App.css";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";
import LabelField from "./labelField";
import { useDispatch } from "react-redux";
import SquareCheckBox from "./SquareCheckBox";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { getExamListForWhatsapp } from "../Action/courseAction";

function ExamAndSubExamPopUp(props) {
  const dispatch = useDispatch();
  const [examList, setExamList] = useState([]);
  const [activeExam, setActiveExam] = useState(null);
  const [activeSubExam, setActiveSubExam] = useState(null);
  const hasSyncedRef = useRef(false);

  // ─── 1. Load exam list on mount ───────────────────────────────────────────
  useEffect(() => {
    dispatch(
      getExamListForWhatsapp((res) => {
        const tempExam = res?.data?.map((item) => ({
          _id: item?._id,
          name: item?.name,
          isSelected: false,

          // subExams with their own baseCourses embedded — NO extra API call needed
          subExamTypeId:
            item?.childrenId?.map((child) => ({
              _id: child?._id,
              name: child?.name,
              isSelected: false,
              baseCourseId:
                child?.baseCourseId?.map((bc) => ({
                  _id: bc?._id,
                  name: bc?.name,
                  isSelected: false,
                  subExamId: child?._id,
                })) || [],
            })) || [],

          // If NO childrenId → use exam's own baseCourseId directly
          baseCourseId:
            !item?.childrenId || item?.childrenId.length === 0
              ? item?.baseCourseId?.map((bc) => ({
                  _id: bc?._id,
                  name: bc?.name,
                  isSelected: false,
                  subExamId: null,
                })) || []
              : [],
        }));

        setExamList(tempExam);
        setActiveExam(null);
        setActiveSubExam(null);
      }),
    );
  }, [dispatch]);

  // ─── 2. Sync selectedList when modal opens ────────────────────────────────
  useEffect(() => {
    if (
      !props.open ||
      hasSyncedRef.current ||
      !props.selectedList ||
      examList.length === 0
    )
      return;

    const updated = examList.map((ex) => {
      const sel = props.selectedList.filter((s) => s.examId === ex._id);

      if (ex.subExamTypeId?.length > 0) {
        const updatedSubExams = ex.subExamTypeId.map((sub) => {
          const updatedBase = sub.baseCourseId.map((bc) => ({
            ...bc,
            isSelected: sel.some(
              (s) => s.baseCourseId === bc._id && s.subExamId === sub._id,
            ),
          }));
          return {
            ...sub,
            isSelected: updatedBase.some((b) => b.isSelected),
            baseCourseId: updatedBase,
          };
        });

        const flatBase = updatedSubExams
          .filter((s) => s.isSelected)
          .flatMap((s) => s.baseCourseId);

        return {
          ...ex,
          subExamTypeId: updatedSubExams,
          baseCourseId: flatBase,
          isSelected: updatedSubExams.some((s) => s.isSelected),
        };
      }

      // no subExams
      const updatedBase = ex.baseCourseId.map((bc) => ({
        ...bc,
        isSelected: sel.some((s) => s.baseCourseId === bc._id),
      }));
      return {
        ...ex,
        baseCourseId: updatedBase,
        isSelected: updatedBase.some((b) => b.isSelected),
      };
    });

    setExamList(updated);

    const examToOpen = updated.find(
      (ex) =>
        ex.isSelected ||
        ex.subExamTypeId?.some((s) => s.isSelected) ||
        ex.baseCourseId?.some((bc) => bc.isSelected),
    );
    if (examToOpen) {
      setActiveExam(examToOpen);
      const subToOpen = examToOpen.subExamTypeId?.find((s) => s.isSelected);
      if (subToOpen) setActiveSubExam(subToOpen);
    }

    hasSyncedRef.current = true;
  }, [props.open, props.selectedList, examList.length]);

  useEffect(() => {
    if (props.open) {
      hasSyncedRef.current = false;
      setActiveSubExam(null);
    }
  }, [props.open]);

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const syncActiveExam = (updatedList, currentExam) => {
    if (!currentExam) return;
    const found = updatedList.find((e) => e._id === currentExam._id);
    if (found) setActiveExam(found);
  };

  const syncActiveSubExam = (updatedSubExams, currentSubExam) => {
    if (!currentSubExam) return;
    const found = updatedSubExams?.find((s) => s._id === currentSubExam._id);
    if (found) setActiveSubExam(found);
  };

  const updateSelected = (updatedList) => {
    const allSelected = updatedList.flatMap((ex) => {
      const hasSubExams = ex.subExamTypeId?.length > 0;

      if (hasSubExams) {
        return ex.subExamTypeId.flatMap((sub) =>
          (sub.baseCourseId || [])
            .filter((bc) => bc.isSelected)
            .map((bc) => ({
              examId: ex._id,
              examName: ex.name,
              subExamId: sub._id,
              subExamName: sub.name,
              baseCourseId: bc._id,
              baseCourseName: bc.name,
            })),
        );
      }

      // no subExams, no baseCourses → plain exam
      if (!ex.baseCourseId || ex.baseCourseId.length === 0) {
        return ex.isSelected
          ? [
              {
                examId: ex._id,
                examName: ex.name,
                subExamId: null,
                subExamName: null,
                baseCourseId: null,
                baseCourseName: null,
              },
            ]
          : [];
      }

      return (ex.baseCourseId || [])
        .filter((bc) => bc.isSelected)
        .map((bc) => ({
          examId: ex._id,
          examName: ex.name,
          subExamId: null,
          subExamName: null,
          baseCourseId: bc._id,
          baseCourseName: bc.name,
        }));
    });

    props.onSelectExams(allSelected);
  };

  // ─── Exam checkbox ────────────────────────────────────────────────────────
  const onSelectExam = (event, index) => {
    const isChecked = event.target.checked;
    const newArray = examList.map((examItem, i) => {
      if (i !== index) return examItem;

      if (examItem.subExamTypeId?.length > 0) {
        const updatedSubExams = examItem.subExamTypeId.map((sub) => ({
          ...sub,
          isSelected: isChecked,
          baseCourseId: sub.baseCourseId.map((bc) => ({
            ...bc,
            isSelected: isChecked,
          })),
        }));
        const flatBase = updatedSubExams.flatMap((s) => s.baseCourseId);
        return {
          ...examItem,
          isSelected: isChecked,
          subExamTypeId: updatedSubExams,
          baseCourseId: flatBase,
        };
      }

      return {
        ...examItem,
        isSelected: isChecked,
        baseCourseId: examItem.baseCourseId.map((bc) => ({
          ...bc,
          isSelected: isChecked,
        })),
      };
    });

    setExamList(newArray);
    const clicked = newArray[index];
    setActiveExam(clicked);
    if (clicked.subExamTypeId?.length > 0) {
      setActiveSubExam(clicked.subExamTypeId[0]);
    } else {
      setActiveSubExam(null);
    }
    updateSelected(newArray);
  };

  const onSelectAllExams = (event) => {
    const isChecked = event.target.checked;
    const newArray = examList.map((examItem) => {
      if (examItem.subExamTypeId?.length > 0) {
        const updatedSubExams = examItem.subExamTypeId.map((sub) => ({
          ...sub,
          isSelected: isChecked,
          baseCourseId: sub.baseCourseId.map((bc) => ({
            ...bc,
            isSelected: isChecked,
          })),
        }));
        return {
          ...examItem,
          isSelected: isChecked,
          subExamTypeId: updatedSubExams,
          baseCourseId: updatedSubExams.flatMap((s) => s.baseCourseId),
        };
      }
      return {
        ...examItem,
        isSelected: isChecked,
        baseCourseId: examItem.baseCourseId.map((bc) => ({
          ...bc,
          isSelected: isChecked,
        })),
      };
    });
    setExamList(newArray);
    syncActiveExam(newArray, activeExam);
    updateSelected(newArray);
  };

  // ─── SubExam checkbox ─────────────────────────────────────────────────────
  const onSelectSubExam = (event, subIndex, currentExam) => {
    const isChecked = event.target.checked;

    const newArray = examList.map((ex) => {
      if (ex._id !== currentExam._id) return ex;

      const updatedSubExams = ex.subExamTypeId.map((sub, i) => {
        if (i !== subIndex) return sub;
        return {
          ...sub,
          isSelected: isChecked,
          baseCourseId: sub.baseCourseId.map((bc) => ({
            ...bc,
            isSelected: isChecked,
          })),
        };
      });

      const flatBase = updatedSubExams
        .filter((s) => s.isSelected)
        .flatMap((s) => s.baseCourseId);

      return {
        ...ex,
        isSelected: updatedSubExams.some((s) => s.isSelected),
        subExamTypeId: updatedSubExams,
        baseCourseId: flatBase,
      };
    });

    setExamList(newArray);
    const freshExam = newArray.find((e) => e._id === currentExam._id);
    if (freshExam) {
      setActiveExam(freshExam);
      syncActiveSubExam(freshExam.subExamTypeId, activeSubExam);
    }
    updateSelected(newArray);
  };

  const onSelectAllSubExams = (event) => {
    const isChecked = event.target.checked;
    if (!activeExam) return;

    const newArray = examList.map((ex) => {
      if (ex._id !== activeExam._id) return ex;

      const updatedSubExams = ex.subExamTypeId.map((sub) => ({
        ...sub,
        isSelected: isChecked,
        baseCourseId: sub.baseCourseId.map((bc) => ({
          ...bc,
          isSelected: isChecked,
        })),
      }));

      return {
        ...ex,
        isSelected: isChecked,
        subExamTypeId: updatedSubExams,
        baseCourseId: updatedSubExams
          .filter((s) => s.isSelected)
          .flatMap((s) => s.baseCourseId),
      };
    });

    setExamList(newArray);
    const freshExam = newArray.find((e) => e._id === activeExam._id);
    if (freshExam) {
      setActiveExam(freshExam);
      syncActiveSubExam(freshExam.subExamTypeId, activeSubExam);
    }
    updateSelected(newArray);
  };

  // ─── BaseCourse (Stream) checkbox ─────────────────────────────────────────
  const onSelectExamStream = (event, bc, currentExam) => {
    const isChecked = event.target.checked;

    const newArray = examList.map((ex) => {
      if (ex._id !== currentExam._id) return ex;

      if (ex.subExamTypeId?.length > 0) {
        // update inside the correct subExam
        const updatedSubExams = ex.subExamTypeId.map((sub) => {
          if (sub._id !== bc.subExamId) return sub;
          const updatedBase = sub.baseCourseId.map((item) =>
            item._id === bc._id ? { ...item, isSelected: isChecked } : item,
          );
          return {
            ...sub,
            isSelected: updatedBase.some((b) => b.isSelected),
            baseCourseId: updatedBase,
          };
        });

        const flatBase = updatedSubExams
          .filter((s) => s.isSelected)
          .flatMap((s) => s.baseCourseId);

        return {
          ...ex,
          isSelected: updatedSubExams.some((s) => s.isSelected),
          subExamTypeId: updatedSubExams,
          baseCourseId: flatBase,
        };
      }

      // no subExams
      const updatedBase = ex.baseCourseId.map((item) =>
        item._id === bc._id ? { ...item, isSelected: isChecked } : item,
      );
      return {
        ...ex,
        isSelected: updatedBase.some((b) => b.isSelected),
        baseCourseId: updatedBase,
      };
    });

    setExamList(newArray);
    const freshExam = newArray.find((e) => e._id === currentExam._id);
    if (freshExam) {
      setActiveExam(freshExam);
      if (activeSubExam) {
        syncActiveSubExam(freshExam.subExamTypeId, activeSubExam);
      }
    }
    updateSelected(newArray);
  };

  const onSelectAllStreams = (event) => {
    const isChecked = event.target.checked;
    if (!activeExam) return;

    const newArray = examList.map((ex) => {
      if (ex._id !== activeExam._id) return ex;

      if (ex.subExamTypeId?.length > 0 && activeSubExam) {
        const updatedSubExams = ex.subExamTypeId.map((sub) => {
          if (sub._id !== activeSubExam._id) return sub;
          return {
            ...sub,
            isSelected: isChecked,
            baseCourseId: sub.baseCourseId.map((bc) => ({
              ...bc,
              isSelected: isChecked,
            })),
          };
        });
        return {
          ...ex,
          isSelected: updatedSubExams.some((s) => s.isSelected),
          subExamTypeId: updatedSubExams,
          baseCourseId: updatedSubExams
            .filter((s) => s.isSelected)
            .flatMap((s) => s.baseCourseId),
        };
      }

      return {
        ...ex,
        isSelected: isChecked,
        baseCourseId: ex.baseCourseId.map((bc) => ({
          ...bc,
          isSelected: isChecked,
        })),
      };
    });

    setExamList(newArray);
    const freshExam = newArray.find((e) => e._id === activeExam._id);
    if (freshExam) {
      setActiveExam(freshExam);
      syncActiveSubExam(freshExam.subExamTypeId, activeSubExam);
    }
    updateSelected(newArray);
  };

  // ─── Computed visible base courses ────────────────────────────────────────
  const visibleBaseCourses = (() => {
    if (!activeExam) return [];

    if (activeExam.subExamTypeId?.length > 0) {
      if (!activeSubExam) return [];
      const freshSub = activeExam.subExamTypeId.find(
        (s) => s._id === activeSubExam._id,
      );
      return freshSub?.baseCourseId || [];
    }

    return activeExam.baseCourseId || [];
  })();

  const showStreamPanel =
    activeExam &&
    (activeExam.subExamTypeId?.length === 0 ? true : activeSubExam !== null);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Modal
      className="model"
      open={props.open}
      onClose={props.handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={props.open}>
        <div
          className="bg-white rounded-4 shadow p-4 w-100"
          style={{ maxWidth: "850px", margin: "auto" }}
        >
          <form>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
              <h5 className="fw-semibold text-dark mb-0">
                Exam Name &amp; Exam Stream
              </h5>
              <ClearOutlinedIcon
                className="text-secondary cursor-pointer"
                onClick={props.handleClose}
              />
            </div>

            <div className="row g-3">
              {/* ── Column 1: Exam List ── */}
              <div
                className={
                  activeExam?.subExamTypeId?.length > 0
                    ? "col-md-4"
                    : "col-md-6"
                }
              >
                <LabelField labelName={"Exam Name"} isRequired />

                {examList?.length > 0 && (
                  <div className="card shadow-sm border-0 mt-2">
                    <div className="d-flex align-items-center px-3 py-2 border-bottom bg-light">
                      <SquareCheckBox
                        id="select-all-exams"
                        checked={
                          examList.length > 0 &&
                          examList.every((e) => e.isSelected)
                        }
                        onChange={onSelectAllExams}
                      />
                      <span className="ms-2 fw-medium">Select All</span>
                    </div>

                    <div
                      className="overflow-auto"
                      style={{ maxHeight: "450px" }}
                    >
                      {examList.map((c, index) => (
                        <div
                          key={c._id}
                          className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom font12"
                        >
                          <SquareCheckBox
                            id={c._id}
                            value={c.name}
                            checked={c.isSelected}
                            onChange={(e) => onSelectExam(e, index)}
                          />
                          <div className="d-flex justify-content-between align-items-center w-100 ms-2 font12">
                            <span
                              className={
                                c._id === activeExam?._id
                                  ? "fw-semibold text-primary"
                                  : ""
                              }
                            >
                              {c.name}
                            </span>
                            {/* Eye icon: sets active exam to show sub/base courses */}
                            <RemoveRedEyeIcon
                              fontSize="small"
                              className={
                                c._id === activeExam?._id
                                  ? "text-primary cursor-pointer"
                                  : "text-muted cursor-pointer"
                              }
                              style={{ fontSize: 18 }}
                              onClick={() => {
                                const found = examList.find(
                                  (ex) => ex._id === c._id,
                                );
                                setActiveExam(found);
                                // auto-select first subExam to show its courses
                                if (found?.subExamTypeId?.length > 0) {
                                  setActiveSubExam(found.subExamTypeId[0]);
                                } else {
                                  setActiveSubExam(null);
                                }
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* ── Column 2: SubExam List (only if active exam has subExams) ── */}
              {activeExam?.subExamTypeId?.length > 0 && (
                <div
                  className={
                    activeExam?.subExamTypeId?.length > 0
                      ? "col-md-4"
                      : "col-md-6"
                  }
                >
                  <LabelField labelName={"Sub Exam Type"} />

                  <div className="card shadow-sm border-0 mt-2">
                    <div className="d-flex align-items-center px-3 py-2 border-bottom bg-light">
                      <SquareCheckBox
                        id="select-all-subexams"
                        checked={
                          activeExam.subExamTypeId.length > 0 &&
                          activeExam.subExamTypeId.every((s) => s.isSelected)
                        }
                        onChange={onSelectAllSubExams}
                      />
                      <span className="ms-2 fw-medium">Select All</span>
                    </div>

                    <div
                      className="overflow-auto"
                      style={{ maxHeight: "450px" }}
                    >
                      {activeExam.subExamTypeId.map((c, index) => (
                        <div
                          key={c._id}
                          className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom font12"
                        >
                          <SquareCheckBox
                            id={`subexam-${c._id}`}
                            value={c.name}
                            checked={c.isSelected}
                            onChange={(e) =>
                              onSelectSubExam(e, index, activeExam)
                            }
                            labelName={c.name}
                          />
                          {/* Eye icon: sets active subExam to show its baseCourses */}
                          <RemoveRedEyeIcon
                            fontSize="small"
                            className={
                              c._id === activeSubExam?._id
                                ? "text-primary cursor-pointer"
                                : "text-muted cursor-pointer"
                            }
                            style={{ fontSize: 18 }}
                            onClick={() => {
                              // find fresh subExam from activeExam in examList
                              const freshExam = examList.find(
                                (ex) => ex._id === activeExam._id,
                              );
                              const freshSub = freshExam?.subExamTypeId?.find(
                                (s) => s._id === c._id,
                              );
                              setActiveSubExam(freshSub || c);
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ── Column 3: BaseCourse (Stream) List ── */}
              {showStreamPanel && visibleBaseCourses.length > 0 && (
                <div
                  className={
                    activeExam?.subExamTypeId?.length > 0
                      ? "col-md-4"
                      : "col-md-6"
                  }
                >
                  <LabelField labelName={"Exam Stream"} />

                  <div className="card shadow-sm border-0 mt-2">
                    <div className="d-flex align-items-center px-3 py-2 border-bottom bg-light">
                      <SquareCheckBox
                        id="select-all-streams"
                        checked={
                          visibleBaseCourses.length > 0 &&
                          visibleBaseCourses.every((bc) => bc.isSelected)
                        }
                        onChange={onSelectAllStreams}
                      />
                      <span className="ms-2 fw-medium">Select All</span>
                    </div>

                    <div
                      className="overflow-auto"
                      style={{ maxHeight: "450px" }}
                    >
                      {visibleBaseCourses.map((c) => (
                        <div
                          key={`${c._id}-${c.subExamId || "base"}`}
                          className="d-flex align-items-center px-3 py-2 border-bottom font12"
                        >
                          <SquareCheckBox
                            id={`${c._id}-${c.subExamId || "base"}`}
                            value={c.name}
                            checked={c.isSelected}
                            onChange={(e) =>
                              onSelectExamStream(e, c, activeExam)
                            }
                            labelName={c.name}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>
      </Fade>
    </Modal>
  );
}

export default ExamAndSubExamPopUp;
